import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Box, Grid, Typography, Paper, Button, TextField, Autocomplete,
  Card, CardContent, CardActionArea, Chip, Divider, Stack,
  Skeleton, Alert, Snackbar, useTheme, useMediaQuery, Fade, Checkbox
} from '@mui/material';
import RocketLaunchRoundedIcon from '@mui/icons-material/RocketLaunchRounded';
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import WorkOutlineRoundedIcon from '@mui/icons-material/WorkOutlineRounded';
import ChecklistRoundedIcon from '@mui/icons-material/ChecklistRounded';
import CodeRoundedIcon from '@mui/icons-material/CodeRounded';
import LayersRoundedIcon from '@mui/icons-material/LayersRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';

import HourglassTopRoundedIcon from '@mui/icons-material/HourglassTopRounded';
import EventRoundedIcon from '@mui/icons-material/EventRounded';

import { useNextGenAuth } from '../context/NextGenAuthContext';
import enrollmentService from '../services/enrollmentService';
import VersionScheduleCard from '../components/Enrollment/VersionScheduleCard';
// Single source of truth: the SAME normalisers the admin wizard and admin
// details page use to read a project row. mergeProjectDetails folds the
// details JSONB overflow into the flat object; toList makes array-ish
// columns safe to .map(). No parallel project model exists on this page.
import { mergeProjectDetails, toList } from '../../SuperAdminComponents/NextGenBuilder/projectWizard/projectFormModel';
import axios from 'axios';
import API_BASE_URL from '../../Config/config';

// ── Add to Calendar helpers (batch start reminder) ──────────────────────────
const calendarStamp = (value) => {
  const d = new Date(value);
  return `${d.getUTCFullYear()}${String(d.getUTCMonth() + 1).padStart(2, '0')}${String(d.getUTCDate()).padStart(2, '0')}T${String(d.getUTCHours()).padStart(2, '0')}${String(d.getUTCMinutes()).padStart(2, '0')}00Z`;
};

const googleCalendarUrl = ({ title, start, end, details }) => {
  const endTime = end || new Date(new Date(start).getTime() + 60 * 60 * 1000);
  return 'https://calendar.google.com/calendar/render?action=TEMPLATE'
    + `&text=${encodeURIComponent(title)}`
    + `&dates=${calendarStamp(start)}/${calendarStamp(endTime)}`
    + `&details=${encodeURIComponent(details || '')}`;
};

const downloadIcs = ({ title, start, end, details }) => {
  const endTime = end || new Date(new Date(start).getTime() + 60 * 60 * 1000);
  const ics = [
    'BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//TheSkillGenie//NextGenBuilder//EN',
    'BEGIN:VEVENT',
    `UID:${Date.now()}@theskillgenie`,
    `DTSTAMP:${calendarStamp(new Date())}`,
    `DTSTART:${calendarStamp(start)}`,
    `DTEND:${calendarStamp(endTime)}`,
    `SUMMARY:${title}`,
    `DESCRIPTION:${(details || '').replace(/\n/g, '\\n')}`,
    'END:VEVENT', 'END:VCALENDAR'
  ].join('\r\n');
  const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${title.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}.ics`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const daysUntil = (value) => {
  if (!value) return null;
  const t = new Date(value).getTime();
  if (Number.isNaN(t)) return null;
  return Math.ceil((t - Date.now()) / (24 * 60 * 60 * 1000));
};

// --- Constants ---
const ROLES = [
  'Frontend Developer',
  'Backend Developer',
  'Full Stack Developer',
  'Mobile Developer',
  'DevOps Engineer',
  'QA / Test Engineer'
];

const PROJECTS = [
  {
    id: 'nexora',
    name: 'Nexora',
    tagline: 'The AI-Powered Student Super Platform',
    icon: <RocketLaunchRoundedIcon sx={{ fontSize: 40, color: '#6C3BFF' }} />,
    tech: ['Flutter', 'Next.js 15', 'React 19', 'TypeScript', 'Node.js 22', 'NestJS', 'PostgreSQL'],
    difficulty: 'Advanced',
    teamSize: '12-15 Engineers',
    duration: '6 Months',
    vision: 'To build a unified digital ecosystem for students worldwide.',
    summary: 'Nexora is an AI-powered student super platform that combines social networking, communication, learning, campus collaboration, creator tools, marketplace, AI assistance, and career opportunities into a single intelligent platform.',
    modules: ['Social Feed', 'Chat & Messaging', 'College Communities', 'Campus Events', 'AI Assistant', 'Marketplace', 'Wallet & Payments', 'Placements', 'Creator Studio'],
    learning: ['Large-scale application architecture', 'Mobile development', 'React & Next.js', 'Backend APIs', 'Authentication', 'Real-time communication', 'PostgreSQL', 'AWS deployment', 'Agile product development'],
    recommendedRole: 'Full Stack / Mobile'
  },
  {
    id: 'nova',
    name: 'Nova',
    tagline: "The World's Smartest AI Browser",
    icon: <AutoAwesomeRoundedIcon sx={{ fontSize: 40, color: '#00D1FF' }} />,
    tech: ['React.js', 'TypeScript', 'NestJS', 'PostgreSQL', 'AI APIs'],
    difficulty: 'Expert',
    teamSize: '8-10 Engineers',
    duration: '4 Months',
    vision: 'Reinventing how humanity interacts with the web using AI.',
    summary: 'Nova is an AI-native browser and productivity workspace that combines AI search, intelligent browsing, note-taking, research, AI writing, coding assistance, and collaboration into a single browser experience.',
    modules: ['AI Browser', 'AI Search', 'Research Workspace', 'AI Coding', 'Notes', 'Productivity', 'Meetings', 'Browser Extensions'],
    learning: ['React', 'Browser APIs', 'AI Integration', 'TypeScript', 'Enterprise UI', 'Authentication', 'REST APIs', 'Product Engineering'],
    recommendedRole: 'Frontend / AI Engineer'
  },
  {
    id: 'libro',
    name: 'Libro',
    tagline: 'AI Learning Marketplace',
    icon: <SchoolOutlinedIcon sx={{ fontSize: 40, color: '#FF4D4D' }} />,
    tech: ['Flutter', 'Next.js', 'Node.js', 'NestJS', 'PostgreSQL'],
    difficulty: 'Intermediate',
    teamSize: '10-12 Engineers',
    duration: '5 Months',
    vision: 'Empowering students through an intelligent peer-to-peer resource economy.',
    summary: 'Libro is an AI-powered educational marketplace where students can buy, sell, rent, exchange, and discover books, notes, courses, digital resources, and AI-powered learning tools.',
    modules: ['Books Marketplace', 'Digital Library', 'AI Tutor', 'Courses', 'Study Groups', 'Creator Platform'],
    learning: ['Marketplace Development', 'Flutter', 'React', 'Backend APIs', 'Payments', 'PostgreSQL', 'Cloud Deployment'],
    recommendedRole: 'Full Stack / Backend'
  },
  {
    id: 'vyra',
    name: 'Vyra',
    tagline: 'Personal AI Health & Fitness Coach',
    icon: <VerifiedUserOutlinedIcon sx={{ fontSize: 40, color: '#27AE60' }} />,
    tech: ['Flutter', 'Next.js', 'Node.js', 'NestJS', 'PostgreSQL', 'Redis', 'Amazon S3'],
    difficulty: 'Intermediate',
    teamSize: '6-8 Engineers',
    duration: '4 Months',
    vision: 'Making professional health coaching accessible to everyone through AI.',
    summary: 'Vyra is an intelligent AI-powered health and fitness platform that delivers personalized coaching, workout plans, nutrition guidance, AI recommendations, and health analytics based on each user\'s goals.',
    modules: ['AI Coach', 'Workout Plans', 'Nutrition', 'Goal Tracking', 'Health Analytics', 'AI Recommendations'],
    learning: ['AI Applications', 'Cloud Services', 'Backend APIs', 'Flutter', 'React', 'Analytics', 'PostgreSQL', 'Redis'],
    recommendedRole: 'Mobile / Backend'
  }
];

// --- Styled Components (Abstracted) ---
const InfoCard = ({ icon, label, value, loading }) => (
  <Paper
    elevation={0}
    sx={{
      p: 2, borderRadius: '16px', bgcolor: '#F8FAFC', border: '1px solid #E2E8F0',
      display: 'flex', flexDirection: 'column', gap: 1
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {React.cloneElement(icon, { sx: { fontSize: 18, color: '#6C3BFF' } })}
      <Typography variant="caption" sx={{ fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        {label}
      </Typography>
    </Box>
    {loading ? <Skeleton width="80%" height={24} /> : (
      <Typography variant="body1" sx={{ fontWeight: 600, color: '#1E293B' }}>
        {value || 'Not Provided'}
      </Typography>
    )}
  </Paper>
);

const SectionHeading = ({ icon, title, subtitle }) => (
  <Box sx={{ mb: 4 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
      <Box sx={{ p: 1, borderRadius: '10px', bgcolor: 'rgba(108, 59, 255, 0.1)', color: '#6C3BFF', display: 'flex' }}>
        {icon}
      </Box>
      <Typography variant="h5" sx={{ fontWeight: 800, color: '#0F172A' }}>{title}</Typography>
    </Box>
    <Typography variant="body2" sx={{ color: '#64748B', maxWidth: 600 }}>{subtitle}</Typography>
  </Box>
);

const NextGenEnrollment = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { nextgen_builder } = useNextGenAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [enrolled, setEnrolled] = useState(null);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [preferredRole, setPreferredRole] = useState(null);
  const [githubUsername, setGithubUsername] = useState('');
  const [githubStatus, setGithubStatus] = useState(null); // null | 'checking' | 'valid' | 'invalid'
  const [submitting, setSubmitting] = useState(false);
  const [submitPhase, setSubmitPhase] = useState(''); // 'Creating Enrollment...' | 'Sending GitHub Invitation...'
  const [ghInvite, setGhInvite] = useState(null); // latest invitation record for this student
  const [retrying, setRetrying] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [projectVersionsReal, setProjectVersionsReal] = useState(null); // real ngb_versions rows for selectedProject
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [waitlisted, setWaitlisted] = useState(null); // { position, projectName } after a full-project enroll
  const [assignedVersionInfo, setAssignedVersionInfo] = useState(null); // batch details for the enrolled view

  useEffect(() => {
    fetchInitialData();
    fetchProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nextgen_builder?.id]);

  // Once a project is picked, fetch its REAL version status (open/full/
  // completed, member counts) instead of guessing from the subscription
  // plan's version count — a version that's already full must never be
  // shown as available here.
  useEffect(() => {
    if (!selectedProject?.id) { setProjectVersionsReal(null); return; }
    let cancelled = false;
    (async () => {
      try {
        setVersionsLoading(true);
        const res = await axios.get(`${API_BASE_URL}/api/nextgen-builder/versions`, {
          params: { projectId: selectedProject.id }
        });
        if (!cancelled && res.data?.success) {
          setProjectVersionsReal(res.data.data || []);
        }
      } catch (err) {
        console.error('[Enrollment] Project versions fetch error:', err);
        if (!cancelled) setProjectVersionsReal([]);
      } finally {
        if (!cancelled) setVersionsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedProject?.id]);

  // Enrolled view: resolve the student's batch (their version's schedule)
  // so the success screen can show "Your batch starts on …" + countdown +
  // Add to Calendar even after a page refresh.
  useEffect(() => {
    const versionId = enrolled?.version_id || enrolled?.assignedVersion?.id;
    const projectId = enrolled?.project_id;
    if (!projectId || !versionId) return;
    if (assignedVersionInfo && String(assignedVersionInfo.id) === String(versionId)) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/api/nextgen-builder/versions`, {
          params: { projectId }
        });
        if (cancelled || !res.data?.success) return;
        const rows = Array.isArray(res.data.data) ? res.data.data : [];
        const mine = rows.find(v => String(v.id) === String(versionId));
        if (mine) setAssignedVersionInfo(mine);
      } catch (err) {
        console.error('[Enrollment] Assigned version fetch error:', err);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enrolled?.version_id, enrolled?.project_id]);

  const fetchProjects = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/nextgen-builder/projects`);
      if (res.data.success) {
        // `success: true` says the CALL worked, not that the payload is a
        // list. `projects` is consumed by .map and .length on the initial
        // render of /nextgen-builder/enrollment, so a non-array here was an
        // immediate route-level crash rather than an empty state.
        // Each row runs through mergeProjectDetails so every wizard field
        // (summary, purpose, objectives, timeline…) is available even if an
        // older API deployment didn't flatten the details overflow.
        const rows = Array.isArray(res.data.data) ? res.data.data : [];
        setProjects(rows.map(mergeProjectDetails));
      }
    } catch (err) {
      console.error('Projects Fetch Error:', err);
    }
  };

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      // Fetch Profile
      try {
        const profileRes = await enrollmentService.getProfile();
        setProfile(profileRes.data);
        if (profileRes.data?.github_username) setGithubUsername(profileRes.data.github_username);
      } catch (profileErr) {
        console.error('Profile Fetch Error:', profileErr);
      }

      // Fetch Enrollment
      const enrollmentRes = await enrollmentService.getEnrollment();
      if (enrollmentRes.success && enrollmentRes.data) {
        setEnrolled(enrollmentRes.data);
        // Load the GitHub invitation status (live-synced with GitHub)
        try {
          const invRes = await axios.get(`${API_BASE_URL}/api/nextgen-builder/github/my-invitation`);
          if (invRes.data.success && invRes.data.data) setGhInvite(invRes.data.data);
        } catch (invErr) {
          console.error('[Enrollment] GitHub invitation status fetch failed:', invErr);
        }
      }
    } catch (err) {
      console.error('Data Fetch Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleValidateGithub = async () => {
    const username = githubUsername.trim();
    if (!username) { setGithubStatus(null); return; }
    try {
      setGithubStatus('checking');
      const res = await enrollmentService.validateGithubUsername(username);
      setGithubStatus(res.data?.valid ? 'valid' : 'invalid');
    } catch (err) {
      // Don't block the student if validation service is unreachable
      setGithubStatus(null);
    }
  };

  const handleEnroll = async () => {
    if (!preferredRole) {
      setSnackbar({ open: true, message: 'Please select your preferred engineering role', severity: 'error' });
      return;
    }
    if (!selectedProject) {
      setSnackbar({ open: true, message: 'Please select a project to enroll', severity: 'error' });
      return;
    }
    if (!githubUsername.trim()) {
      setSnackbar({ open: true, message: 'Please enter your GitHub username for repository access', severity: 'error' });
      return;
    }
    if (githubStatus === 'invalid') {
      setSnackbar({ open: true, message: 'GitHub username not found. Please double-check it.', severity: 'error' });
      return;
    }
    const subscriptionActive = profile?.subscriptionActive
      || (profile?.subscription_valid_till && new Date(profile.subscription_valid_till) > new Date());
    if (!subscriptionActive) {
        setSnackbar({ open: true, message: 'Active subscription required for project enrollment', severity: 'warning' });
        return;
    }

    let phaseTimer;
    try {
      setSubmitting(true);
      setSubmitPhase('Creating Enrollment...');
      // Flip the label while the backend moves on to the GitHub invitation step
      phaseTimer = setTimeout(() => setSubmitPhase('Sending GitHub Invitation...'), 1500);

      const payload = {
        projectId: selectedProject.id,
        preferredRole: preferredRole,
        githubUsername: githubUsername.trim()
      };
      console.log('[Enrollment] Submitting enrollment:', payload, 'builder:', nextgen_builder?.id);
      const res = await enrollmentService.submitEnrollment(payload);
      console.log('[Enrollment] Enrollment response:', res);
      if (res.success && res.data?.waitlisted) {
        // Every version of this project is full/closed — student is parked on
        // the waiting list and will be auto-enrolled when a version opens.
        setWaitlisted({
          position: res.data.waitlistPosition,
          projectName: res.data.projectName || selectedProject?.name,
          alreadyWaiting: res.data.alreadyWaiting
        });
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
      if (res.success) {
        setEnrolled(res.data);
        if (res.data?.assignedVersion) {
          const av = res.data.assignedVersion;
          setAssignedVersionInfo(prev => prev || {
            id: av.id, name: av.name,
            start_date: av.startDate, end_date: av.endDate, kickoff_date: av.kickoffDate,
            state: { startsInDays: av.startsInDays, seatsFilled: av.seatsFilled, maxSeats: av.maxSeats }
          });
        }
        setGhInvite({
          status: res.data.githubInvitationStatus,
          github_username: res.data.githubUsername,
          repository_owner: res.data.repository ? res.data.repository.split('/')[0] : null,
          repository_name: res.data.repository ? res.data.repository.split('/')[1] : null,
          failed_reason: res.data.githubFailureReason,
          id: res.data.githubInvitationRecordId,
          requested_at: new Date().toISOString()
        });
        // Auto-assignment explanation outranks the generic message: the
        // student should immediately understand WHY they landed in V0.3.
        const autoMsg = res.data.previousVersionsFull && res.data.assignedVersion
          ? `You have been automatically enrolled into ${res.data.assignedVersion.name} because previous versions are already full.`
          : null;
        setSnackbar({
          open: true,
          message: autoMsg || res.data.message || 'Enrollment Submitted Successfully!',
          severity: ['INVITED', 'ACCEPTED'].includes(res.data.githubInvitationStatus) ? 'success' : 'warning'
        });
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch (err) {
      console.error('[Enrollment] Enrollment failed:', err);
      setSnackbar({ open: true, message: err.message || 'Enrollment failed', severity: 'error' });
    } finally {
      clearTimeout(phaseTimer);
      setSubmitting(false);
      setSubmitPhase('');
    }
  };

  const handleRetryInvitation = async () => {
    try {
      setRetrying(true);
      const res = await axios.post(`${API_BASE_URL}/api/nextgen-builder/github/invitations/retry`, {
        invitationId: ghInvite?.id || undefined
      });
      console.log('[Enrollment] Retry invitation response:', res.data);
      if (res.data.success) {
        const o = res.data.data;
        setGhInvite(prev => ({
          ...(prev || {}),
          ...(o.record || {}),
          status: o.status,
          failed_reason: o.reason || o.record?.failed_reason || null
        }));
        setSnackbar({
          open: true,
          message: ['INVITED', 'ACCEPTED'].includes(o.status) ? 'GitHub invitation sent successfully!' : (o.reason || 'Retry failed'),
          severity: ['INVITED', 'ACCEPTED'].includes(o.status) ? 'success' : 'error'
        });
      }
    } catch (err) {
      console.error('[Enrollment] Retry failed:', err);
      setSnackbar({ open: true, message: err.response?.data?.message || 'Retry failed', severity: 'error' });
    } finally {
      setRetrying(false);
    }
  };

  // ── Waiting list state: every version of the chosen project is full ──
  if (waitlisted) {
    return (
      <Box sx={{ p: { xs: 2, md: 4 }, minHeight: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}>
          <Paper elevation={0} sx={{ p: 6, borderRadius: '24px', textAlign: 'center', maxWidth: 560, border: '1px solid #E2E8F0', boxShadow: '0 20px 50px rgba(0,0,0,0.05)' }}>
            <HourglassTopRoundedIcon sx={{ fontSize: 72, color: '#F59E0B', mb: 2 }} />
            <Typography variant="h4" sx={{ fontWeight: 800, color: '#0F172A', mb: 1.5 }}>
              You're on the Waiting List
            </Typography>
            <Typography variant="body1" sx={{ color: '#64748B', mb: 3, lineHeight: 1.7 }}>
              This project is currently full. You have been added to the waiting list.
              You will automatically be enrolled into the next available version.
            </Typography>
            <Box sx={{ bgcolor: '#FFFBEB', border: '1px solid #FDE68A', p: 2.5, borderRadius: '16px', mb: 4 }}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#92400E', textTransform: 'uppercase' }}>Project</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 800, color: '#78350F' }}>{waitlisted.projectName}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#92400E', textTransform: 'uppercase' }}>Queue Position</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 800, color: '#78350F' }}>#{waitlisted.position || '—'}</Typography>
                </Grid>
              </Grid>
            </Box>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
              <Button variant="contained" onClick={() => { window.location.href = '/nextgen-builder/dashboard'; }}
                sx={{ borderRadius: '12px', px: 4, py: 1.5, textTransform: 'none', fontWeight: 700, bgcolor: '#6C3BFF', '&:hover': { bgcolor: '#5A2EE5' } }}>
                Open Dashboard
              </Button>
              <Button variant="outlined" onClick={() => setWaitlisted(null)}
                sx={{ borderRadius: '12px', px: 4, py: 1.5, textTransform: 'none', fontWeight: 700, borderColor: '#6C3BFF', color: '#6C3BFF' }}>
                Browse Other Projects
              </Button>
            </Stack>
          </Paper>
        </motion.div>
      </Box>
    );
  }

  if (enrolled) {
    const proj = PROJECTS.find(p => p.id === enrolled.project_id) || { name: enrolled.project_name || 'Assigned Project' };
    const ghStatus = ghInvite?.status || enrolled.githubInvitationStatus || enrolled.github_invite_status || null;
    const ghRepo = ghInvite?.repository_owner && ghInvite?.repository_name
      ? `${ghInvite.repository_owner}/${ghInvite.repository_name}`
      : (enrolled.repository || null);
    const ghUser = ghInvite?.github_username || enrolled.githubUsername || enrolled.github_username || null;
    const ghOk = ['INVITED', 'ACCEPTED'].includes(ghStatus);
    const ghFailed = ['FAILED', 'EXPIRED'].includes(ghStatus);
    return (
      <Box sx={{ p: { xs: 2, md: 4 }, minHeight: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}>
          <Paper elevation={0} sx={{ p: 6, borderRadius: '24px', textAlign: 'center', maxWidth: 600, border: '1px solid #E2E8F0', boxShadow: '0 20px 50px rgba(0,0,0,0.05)' }}>
            <Box sx={{ mb: 3 }}>
              <CheckCircleRoundedIcon sx={{ fontSize: 80, color: '#27AE60' }} />
            </Box>
            <Typography variant="h4" sx={{ fontWeight: 800, color: '#0F172A', mb: 1 }}>Enrollment Successful</Typography>
            <Typography variant="body1" sx={{ color: '#64748B', mb: 4 }}>You have successfully applied for the project. Our team will review your profile and assign you to a professional engineering team soon.</Typography>

            <Box sx={{ bgcolor: '#F8FAFC', p: 3, borderRadius: '16px', textAlign: 'left', mb: 4, border: '1px solid #E2E8F0' }}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase' }}>Selected Project</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 700, color: '#1E293B' }}>{proj.name}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase' }}>Preferred Role</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 700, color: '#1E293B' }}>{enrolled.preferred_role}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase' }}>Status</Typography>
                  <Chip label={enrolled.status} size="small" sx={{ fontWeight: 700, bgcolor: 'rgba(108, 59, 255, 0.1)', color: '#6C3BFF', mt: 0.5 }} />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase' }}>Enrollment Date</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 700, color: '#1E293B' }}>{new Date(enrolled.created_at).toLocaleDateString()}</Typography>
                </Grid>
              </Grid>
            </Box>

            {/* Your Batch — future versions get a start date, countdown and calendar links */}
            {assignedVersionInfo && (() => {
              const av = assignedVersionInfo;
              const startsIn = av.state?.startsInDays ?? daysUntil(av.start_date);
              const isFuture = startsIn != null && startsIn > 0;
              const startDate = av.start_date ? new Date(av.start_date).toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' }) : null;
              return (
                <Box sx={{
                  p: 3, borderRadius: '16px', textAlign: 'left', mb: 4,
                  bgcolor: 'rgba(108, 59, 255, 0.05)', border: '1px solid rgba(108, 59, 255, 0.25)'
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <EventRoundedIcon sx={{ color: '#6C3BFF' }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#0F172A' }}>
                      Your Batch — {av.name}
                    </Typography>
                    {isFuture && (
                      <Chip size="small" label="Coming Soon"
                        sx={{ ml: 'auto', bgcolor: '#DBEAFE', color: '#1D4ED8', fontWeight: 800, fontSize: '10px' }} />
                    )}
                  </Box>
                  {startDate ? (
                    <Typography variant="body2" sx={{ color: '#475569', mb: isFuture ? 1 : 0 }}>
                      Your batch starts on <b>{startDate}</b>.
                    </Typography>
                  ) : (
                    <Typography variant="body2" sx={{ color: '#475569' }}>
                      Your batch start date will be announced soon.
                    </Typography>
                  )}
                  {isFuture && (
                    <Typography variant="body2" sx={{ color: '#6C3BFF', fontWeight: 800, mb: 1.5 }}>
                      {startsIn} day{startsIn === 1 ? '' : 's'} remaining
                    </Typography>
                  )}
                  {av.start_date && (
                    <Stack direction="row" spacing={1.5} sx={{ mt: 1 }}>
                      <Button size="small" variant="outlined"
                        onClick={() => downloadIcs({
                          title: `${proj.name} ${av.name} — Batch Starts`,
                          start: av.start_date, end: av.end_date,
                          details: `Your NextGen Builder batch ${av.name} for ${proj.name} begins.`
                        })}
                        sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 700, borderColor: '#6C3BFF', color: '#6C3BFF' }}>
                        Add to Calendar (.ics)
                      </Button>
                      <Button size="small" variant="outlined" component="a" target="_blank" rel="noopener noreferrer"
                        href={googleCalendarUrl({
                          title: `${proj.name} ${av.name} — Batch Starts`,
                          start: av.start_date, end: av.end_date,
                          details: `Your NextGen Builder batch ${av.name} for ${proj.name} begins.`
                        })}
                        sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 700, borderColor: '#6C3BFF', color: '#6C3BFF' }}>
                        Google Calendar
                      </Button>
                    </Stack>
                  )}
                </Box>
              );
            })()}

            {/* GitHub Access status */}
            {ghUser && (
              <Box sx={{
                p: 3, borderRadius: '16px', textAlign: 'left', mb: 4,
                bgcolor: ghOk ? 'rgba(39, 174, 96, 0.06)' : ghFailed ? 'rgba(220, 38, 38, 0.05)' : '#F8FAFC',
                border: '1px solid', borderColor: ghOk ? 'rgba(39, 174, 96, 0.3)' : ghFailed ? 'rgba(220, 38, 38, 0.25)' : '#E2E8F0'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <CodeRoundedIcon sx={{ color: ghOk ? '#27AE60' : ghFailed ? '#DC2626' : '#64748B' }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#0F172A' }}>GitHub Access</Typography>
                  <Chip
                    size="small"
                    label={
                      ghStatus === 'ACCEPTED' ? 'Collaborator Active' :
                      ghStatus === 'INVITED' ? 'Invitation Sent' :
                      ghFailed ? 'Invitation Failed' : 'Pending'
                    }
                    sx={{
                      fontWeight: 700, ml: 'auto',
                      bgcolor: ghStatus === 'ACCEPTED' ? '#dcfce7' : ghStatus === 'INVITED' ? '#fef9c3' : ghFailed ? '#fef2f2' : '#f1f5f9',
                      color: ghStatus === 'ACCEPTED' ? '#166534' : ghStatus === 'INVITED' ? '#854d0e' : ghFailed ? '#991b1b' : '#64748b'
                    }}
                  />
                </Box>
                <Grid container spacing={1.5}>
                  {ghRepo && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase' }}>Repository</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#1E293B' }}>{ghRepo}</Typography>
                    </Grid>
                  )}
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase' }}>GitHub Username</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#1E293B' }}>@{ghUser}</Typography>
                  </Grid>
                </Grid>
                {ghStatus === 'INVITED' && (
                  <Typography variant="body2" sx={{ color: '#854d0e', mt: 1.5 }}>
                    Check your GitHub email and accept the invitation to get repository access.
                  </Typography>
                )}
                {ghFailed && (
                  <>
                    <Typography variant="body2" sx={{ color: '#991b1b', mt: 1.5 }}>
                      GitHub invitation could not be sent{(ghInvite?.failed_reason || enrolled.githubFailureReason) ? `: ${ghInvite?.failed_reason || enrolled.githubFailureReason}` : '.'}
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      disabled={retrying}
                      onClick={handleRetryInvitation}
                      sx={{ mt: 1.5, borderRadius: '10px', textTransform: 'none', fontWeight: 700, borderColor: '#DC2626', color: '#DC2626' }}
                    >
                      {retrying ? 'Retrying...' : 'Retry Invitation'}
                    </Button>
                  </>
                )}
              </Box>
            )}

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
              <Button variant="contained" onClick={() => { window.location.href = '/nextgen-builder/dashboard'; }} sx={{ borderRadius: '12px', px: 4, py: 1.5, textTransform: 'none', fontWeight: 700, bgcolor: '#6C3BFF', '&:hover': { bgcolor: '#5A2EE5' } }}>
                Open Dashboard
              </Button>
              {ghRepo && ghOk && (
                <Button variant="outlined" component="a" href={`https://github.com/${ghRepo}`} target="_blank" sx={{ borderRadius: '12px', px: 4, py: 1.5, textTransform: 'none', fontWeight: 700, borderColor: '#6C3BFF', color: '#6C3BFF' }}>
                  View Repository
                </Button>
              )}
            </Stack>
          </Paper>
        </motion.div>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      {/* Hero Section */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
        <Paper elevation={0} sx={{
          p: { xs: 4, md: 8 }, borderRadius: '32px', mb: 6,
          background: 'linear-gradient(135deg, #6C3BFF 0%, #3D1E99 100%)',
          color: 'white', position: 'relative', overflow: 'hidden'
        }}>
          <Box sx={{ position: 'relative', zIndex: 2, maxWidth: 800 }}>
            <Chip
              icon={<RocketLaunchRoundedIcon sx={{ fontSize: '16px !important', color: 'white !important' }} />}
              label={`Limited Intake - Cohort ${new Date().getFullYear()}`}
              sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: 'white', fontWeight: 700, mb: 3, backdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.2)' }}
            />
            <Typography variant="h2" sx={{ fontWeight: 900, mb: 2, lineHeight: 1.1, fontSize: { xs: '2.5rem', md: '3.75rem' } }}>
              Build Real Products. <br />
              <span style={{ color: '#00D1FF' }}>Learn Like an Industry Engineer.</span>
            </Typography>
            <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.8)', fontWeight: 500, mb: 4, maxWidth: 650 }}>
              Join one of our flagship products, choose your preferred engineering role, collaborate with experienced developers, participate in agile development, and build production-grade software.
            </Typography>
            <Stack direction="row" spacing={2}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckCircleRoundedIcon sx={{ color: '#00D1FF' }} />
                <Typography variant="body2" sx={{ fontWeight: 600 }}>AI Code Reviews</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckCircleRoundedIcon sx={{ color: '#00D1FF' }} />
                <Typography variant="body2" sx={{ fontWeight: 600 }}>Agile Workflow</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckCircleRoundedIcon sx={{ color: '#00D1FF' }} />
                <Typography variant="body2" sx={{ fontWeight: 600 }}>Industry Mentors</Typography>
              </Box>
            </Stack>
          </Box>
          <Box sx={{
            position: 'absolute', right: -50, bottom: -50, opacity: 0.1,
            transform: 'rotate(-15deg)', display: { xs: 'none', md: 'block' }
          }}>
            <RocketLaunchRoundedIcon sx={{ fontSize: 400 }} />
          </Box>
        </Paper>
      </motion.div>

      <Grid container spacing={6}>
        {/* Section 1: User Info */}
        <Grid item xs={12}>
          <SectionHeading
            icon={<AccountCircleOutlinedIcon />}
            title="Your Information"
            subtitle="Verify your details before enrolling. These details will be used for team assignment and certification."
          />
          <Grid container spacing={2}>
            {[
              { label: 'Full Name', value: profile?.full_name, icon: <PersonOutlineRoundedIcon /> },
              { label: 'Email Address', value: profile?.email, icon: <EmailOutlinedIcon /> },
              { label: 'Mobile Number', value: profile?.mobile_number, icon: <PhoneOutlinedIcon /> },
              { label: 'Username', value: profile?.username, icon: <AccountCircleOutlinedIcon /> },
              { label: 'College', value: profile?.college || profile?.college_name, icon: <SchoolOutlinedIcon /> },
              { label: 'Current Subscription', value: profile?.subscription_plan || profile?.current_subscription || 'Free', icon: <VerifiedUserOutlinedIcon /> },
            ].map((field, i) => (
              <Grid item xs={12} sm={6} md={4} key={i}>
                <InfoCard {...field} loading={loading} />
              </Grid>
            ))}
          </Grid>
        </Grid>

        {/* Section 2: Area of Interest */}
        <Grid item xs={12} md={6}>
          <SectionHeading
            icon={<WorkOutlineRoundedIcon />}
            title="Area of Interest"
            subtitle="Choose the engineering role you would like to contribute to in this project."
          />
          <Autocomplete
            options={ROLES}
            value={preferredRole}
            onChange={(_, newValue) => setPreferredRole(newValue)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Your Preferred Role"
                placeholder="e.g. Frontend Developer"
                required
                helperText="Choose the role that matches your skills and career goals."
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px', bgcolor: 'white',
                    '& fieldset': { borderColor: '#E2E8F0' },
                    '&:hover fieldset': { borderColor: '#6C3BFF' },
                    '&.Mui-focused fieldset': { borderColor: '#6C3BFF' }
                  }
                }}
              />
            )}
          />
        </Grid>

        {/* Section 2b: GitHub Access */}
        <Grid item xs={12} md={6}>
          <SectionHeading
            icon={<CodeRoundedIcon />}
            title="GitHub Access"
            subtitle="Enter your GitHub username. After enrollment, you'll receive an official GitHub invitation email to join the project repository — accept it to get access."
          />
          <TextField
            fullWidth
            label="GitHub Username"
            placeholder="e.g. octocat"
            required
            value={githubUsername}
            onChange={(e) => { setGithubUsername(e.target.value); setGithubStatus(null); }}
            onBlur={handleValidateGithub}
            error={githubStatus === 'invalid'}
            helperText={
              githubStatus === 'checking' ? 'Checking username on GitHub...' :
              githubStatus === 'valid' ? '✓ GitHub username verified' :
              githubStatus === 'invalid' ? 'Username not found on GitHub' :
              'Your invitation to the evolvexinnovations repository will be sent to this account.'
            }
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px', bgcolor: 'white',
                '& fieldset': { borderColor: githubStatus === 'valid' ? '#27AE60' : '#E2E8F0' },
                '&:hover fieldset': { borderColor: '#6C3BFF' },
                '&.Mui-focused fieldset': { borderColor: '#6C3BFF' }
              },
              '& .MuiFormHelperText-root': { color: githubStatus === 'valid' ? '#27AE60' : undefined }
            }}
          />
        </Grid>

        {/* Section 3: Select Project */}
        <Grid item xs={12}>
          <SectionHeading
            icon={<LayersRoundedIcon />}
            title="Select Project"
            subtitle="Choose a flagship product you want to work on. Each project offers unique challenges and learning opportunities."
          />
          <Grid container spacing={3}>
            {projects.map((project) => (
              <Grid item xs={12} sm={6} lg={3} key={project.id}>
                <motion.div whileHover={{ y: -8 }} transition={{ duration: 0.2 }}>
                  <Card
                    elevation={0}
                    sx={{
                      borderRadius: '24px', border: '2px solid',
                      borderColor: selectedProject?.id === project.id ? '#6C3BFF' : '#E2E8F0',
                      transition: '0.3s', position: 'relative',
                      overflow: 'visible'
                    }}
                  >
                    {selectedProject?.id === project.id && (
                      <Box sx={{ position: 'absolute', top: -12, right: 20, zIndex: 3 }}>
                        <Chip label="Selected" size="small" sx={{ bgcolor: '#6C3BFF', color: 'white', fontWeight: 800, border: '2px solid white' }} />
                      </Box>
                    )}
                    <CardActionArea
                      onClick={() => setSelectedProject(project)}
                      sx={{ p: 1, borderRadius: '22px' }}
                    >
                      <CardContent sx={{ p: 2 }}>
                        <Box sx={{ mb: 3 }}>
                          {project.logo_url ? (
                            <img src={project.logo_url} alt={project.name} style={{ width: 40, height: 40, borderRadius: 8 }} />
                          ) : (
                            <RocketLaunchRoundedIcon sx={{ fontSize: 40, color: '#6C3BFF' }} />
                          )}
                        </Box>
                        <Typography variant="h6" sx={{ fontWeight: 800, mb: 0.5, color: '#0F172A' }}>{project.name}</Typography>
                        <Typography variant="body2" sx={{ color: '#64748B', mb: 2, height: 40, overflow: 'hidden' }}>{project.project_summary || project.description}</Typography>

                        <Stack spacing={1.5} sx={{ mb: 3 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LayersRoundedIcon sx={{ fontSize: 16, color: '#94A3B8' }} />
                            <Typography variant="caption" sx={{ fontWeight: 600, color: '#475569' }}>{project.framework}</Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <GroupsRoundedIcon sx={{ fontSize: 16, color: '#94A3B8' }} />
                            <Typography variant="caption" sx={{ fontWeight: 600, color: '#475569' }}>{project.default_team_size} Engineers</Typography>
                          </Box>
                        </Stack>

                        <Divider sx={{ mb: 2, borderStyle: 'dashed' }} />

                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2, height: 50, overflow: 'hidden' }}>
                          {(Array.isArray(project.tags) ? project.tags.slice(0, 3) : []).map((t, i) => (
                            <Chip key={i} label={t} size="small" sx={{ fontSize: '10px', height: 20, bgcolor: '#F1F5F9', color: '#64748B', fontWeight: 600 }} />
                          ))}
                        </Box>

                        <Button
                          fullWidth
                          variant={selectedProject?.id === project.id ? "contained" : "outlined"}
                          sx={{
                            borderRadius: '10px', textTransform: 'none', fontWeight: 700,
                            bgcolor: selectedProject?.id === project.id ? '#6C3BFF' : 'transparent',
                            borderColor: '#6C3BFF', color: selectedProject?.id === project.id ? 'white' : '#6C3BFF'
                          }}
                        >
                          {selectedProject?.id === project.id ? 'Project Selected' : 'View Details'}
                        </Button>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </motion.div>
              </Grid>
            ))}
            {projects.length === 0 && !loading && (
              <Grid item xs={12}>
                <Paper elevation={0} sx={{ p: 6, borderRadius: '24px', border: '1px dashed #E2E8F0', textAlign: 'center', bgcolor: '#F8FAFC' }}>
                  <LayersRoundedIcon sx={{ fontSize: 48, color: '#CBD5E1', mb: 1 }} />
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#94A3B8' }}>No projects are open for enrollment yet</Typography>
                  <Typography variant="body2" sx={{ color: '#CBD5E1' }}>Projects published by the admin team will appear here.</Typography>
                </Paper>
              </Grid>
            )}
          </Grid>
        </Grid>

        {/* Expanded Project Overview — the accordion body of the selected
            card. EVERY value renders from the same Project entity the admin
            wizard saved (SSOT). Empty fields either collapse their section or
            read "Not provided" — nothing is hardcoded. */}
        <AnimatePresence mode="wait">
          {selectedProject && (() => {
            const p = selectedProject;
            const prereqs = toList(p.prerequisites);
            const skills = toList(p.recommended_skills);
            const roles = toList(p.preferred_roles);
            const techChips = Array.from(new Set([
              ...(Array.isArray(p.tech) ? p.tech : []),
              ...toList(p.tech_stacks), ...toList(p.languages),
              ...toList(p.cloud_platforms), ...toList(p.cicd_tools), ...toList(p.ai_tools),
            ].map(String).filter(Boolean)));
            const purposeItems = [
              { label: 'Problem Statement', value: p.problem_statement },
              { label: 'Business Value', value: p.business_value },
              { label: 'Student Benefits', value: p.student_benefits },
              { label: 'Career Benefits', value: p.career_benefits },
            ].filter(i => i.value);
            const start = p.start_date ? new Date(p.start_date) : null;
            const end = p.end_date ? new Date(p.end_date) : null;
            const now = new Date();
            const durationDays = start && end ? Math.max(0, Math.ceil((end - start) / 86400000)) : null;
            const remainingDays = end ? Math.max(0, Math.ceil((end - now) / 86400000)) : null;
            const progressPct = start && end
              ? (now < start ? 0 : now > end ? 100 : Math.round(((now - start) / (end - start)) * 100))
              : null;
            const timelineLabel = !start ? null : now < start ? 'Upcoming' : (end && now > end) ? 'Completed' : 'Running';
            const fmt = (d) => d ? new Date(d).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : 'TBD';
            const splitLines = (s) => String(s || '').split('\n').map(x => x.trim()).filter(Boolean);
            const TimelineRow = ({ label, value }) => (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="#64748B">{label}</Typography>
                <Typography variant="body2" fontWeight="700" color="#1E293B">{value}</Typography>
              </Box>
            );
            return (
              <Grid item xs={12} component={motion.div}
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.3, ease: 'easeOut' }}
              >
                <Paper elevation={0} sx={{ p: { xs: 3, md: 5 }, borderRadius: '32px', border: '1px solid #E2E8F0', bgcolor: 'white', boxShadow: '0 20px 40px rgba(0,0,0,0.03)', overflow: 'hidden' }}>
                  {/* Header + collapse */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                    <Box sx={{ p: 1.5, borderRadius: '16px', bgcolor: '#F8FAFC' }}>
                      {p.logo_url ? (
                        <img src={p.logo_url} alt={p.name} style={{ width: 48, height: 48, borderRadius: 12 }} />
                      ) : (
                        <RocketLaunchRoundedIcon sx={{ fontSize: 32, color: '#6C3BFF' }} />
                      )}
                    </Box>
                    <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                      <Typography variant="h4" sx={{ fontWeight: 800, color: '#0F172A' }}>{p.name}</Typography>
                      <Typography variant="subtitle1" sx={{ color: '#6C3BFF', fontWeight: 600 }}>
                        {p.project_summary || p.tagline || p.framework || 'Project overview'}
                      </Typography>
                    </Box>
                    <Button size="small" onClick={() => setSelectedProject(null)}
                      sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 700, color: '#64748B', flexShrink: 0 }}>
                      Collapse
                    </Button>
                  </Box>

                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '7fr 5fr' }, gap: 4, alignItems: 'start' }}>
                    <Box sx={{ minWidth: 0 }}>
                      {purposeItems.length > 0 && (
                        <Box sx={{ mb: 4 }}>
                          <Typography variant="h6" sx={{ fontWeight: 800, mb: 1.5 }}>Project Purpose</Typography>
                          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                            {purposeItems.map((item) => (
                              <Box key={item.label}>
                                <Paper elevation={0} sx={{ p: 2, borderRadius: '16px', bgcolor: '#F8FAFC', border: '1px solid #E2E8F0', height: '100%' }}>
                                  <Typography variant="caption" sx={{ fontWeight: 800, color: '#94A3B8', textTransform: 'uppercase' }}>{item.label}</Typography>
                                  <Typography variant="body2" sx={{ color: '#475569', mt: 0.5, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{item.value}</Typography>
                                </Paper>
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      )}

                      {p.vision && (
                        <>
                          <Typography variant="h6" sx={{ fontWeight: 800, mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <RocketLaunchRoundedIcon sx={{ color: '#6C3BFF' }} /> Vision
                          </Typography>
                          <Typography variant="body1" sx={{ color: '#475569', mb: 3, lineHeight: 1.7, fontStyle: 'italic' }}>{p.vision}</Typography>
                        </>
                      )}

                      <Typography variant="h6" sx={{ fontWeight: 800, mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AutoAwesomeRoundedIcon sx={{ color: '#F1C40F' }} /> Detailed Description
                      </Typography>
                      <Typography variant="body1" sx={{ color: '#475569', mb: 4, lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
                        {p.description || p.summary || 'Not provided'}
                      </Typography>

                      {techChips.length > 0 && (
                        <>
                          <Typography variant="h6" sx={{ fontWeight: 800, mb: 2 }}>Technology Stack</Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 4 }}>
                            {techChips.map((t) => (
                              <Chip key={t} label={t} sx={{ fontWeight: 600, bgcolor: '#F8FAFC', border: '1px solid #E2E8F0' }} />
                            ))}
                          </Box>
                        </>
                      )}

                      {(p.learning_objectives || p.expected_outcomes) && (
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 3 }}>
                          {p.learning_objectives && (
                            <Box sx={{ minWidth: 0 }}>
                              <Typography variant="h6" sx={{ fontWeight: 800, mb: 1.5 }}>Learning Objectives</Typography>
                              <Stack spacing={1}>
                                {splitLines(p.learning_objectives).map((o, i) => (
                                  <Box key={i} sx={{ display: 'flex', gap: 1.25 }}>
                                    <CheckCircleRoundedIcon sx={{ fontSize: 17, color: '#6C3BFF', mt: '2px' }} />
                                    <Typography variant="body2" sx={{ color: '#475569' }}>{o}</Typography>
                                  </Box>
                                ))}
                              </Stack>
                            </Box>
                          )}
                          {p.expected_outcomes && (
                            <Box sx={{ minWidth: 0 }}>
                              <Typography variant="h6" sx={{ fontWeight: 800, mb: 1.5 }}>Expected Outcomes</Typography>
                              <Stack spacing={1}>
                                {splitLines(p.expected_outcomes).map((o, i) => (
                                  <Box key={i} sx={{ display: 'flex', gap: 1.25 }}>
                                    <CheckCircleRoundedIcon sx={{ fontSize: 17, color: '#27AE60', mt: '2px' }} />
                                    <Typography variant="body2" sx={{ color: '#475569' }}>{o}</Typography>
                                  </Box>
                                ))}
                              </Stack>
                            </Box>
                          )}
                        </Box>
                      )}

                      {(prereqs.length > 0 || skills.length > 0) && (
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                          {prereqs.length > 0 && (
                            <Box sx={{ minWidth: 0 }}>
                              <Typography variant="h6" sx={{ fontWeight: 800, mb: 1.5 }}>Prerequisites</Typography>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {prereqs.map((x) => (
                                  <Chip key={x} label={x} size="small" sx={{ bgcolor: '#FEF3C7', color: '#92400E', fontWeight: 600 }} />
                                ))}
                              </Box>
                            </Box>
                          )}
                          {skills.length > 0 && (
                            <Box sx={{ minWidth: 0 }}>
                              <Typography variant="h6" sx={{ fontWeight: 800, mb: 1.5 }}>Skills You'll Gain</Typography>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {skills.map((x) => (
                                  <Chip key={x} label={x} size="small" sx={{ bgcolor: '#DCFCE7', color: '#166534', fontWeight: 600 }} />
                                ))}
                              </Box>
                            </Box>
                          )}
                        </Box>
                      )}
                    </Box>

                    <Box sx={{ minWidth: 0 }}>
                      <Stack spacing={3}>
                        {/* Project Timeline */}
                        <Box sx={{ p: 3, borderRadius: '24px', bgcolor: '#F8FAFC', border: '1px solid #E2E8F0' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                            <Typography variant="h6" sx={{ fontWeight: 800 }}>Project Timeline</Typography>
                            {timelineLabel && (
                              <Chip size="small" label={timelineLabel} sx={{
                                fontWeight: 800, fontSize: '10px',
                                bgcolor: timelineLabel === 'Running' ? '#EDE9FE' : timelineLabel === 'Upcoming' ? '#DBEAFE' : '#F1F5F9',
                                color: timelineLabel === 'Running' ? '#5B21B6' : timelineLabel === 'Upcoming' ? '#1D4ED8' : '#64748B'
                              }} />
                            )}
                          </Box>
                          {progressPct != null && (
                            <Box sx={{ mb: 2 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                <Typography variant="caption" sx={{ fontWeight: 700, color: '#64748B' }}>Progress</Typography>
                                <Typography variant="caption" sx={{ fontWeight: 800, color: '#0F172A' }}>{progressPct}%</Typography>
                              </Box>
                              <Box sx={{ height: 8, bgcolor: '#E2E8F0', borderRadius: 4, overflow: 'hidden' }}>
                                <Box sx={{ width: `${progressPct}%`, height: '100%', bgcolor: '#6C3BFF', borderRadius: 4 }} />
                              </Box>
                            </Box>
                          )}
                          <Stack spacing={1.25}>
                            <TimelineRow label="Start Date" value={fmt(p.start_date)} />
                            <TimelineRow label="End Date" value={fmt(p.end_date)} />
                            <TimelineRow label="Enrollment Opens" value={fmt(p.enrollment_opens)} />
                            <TimelineRow label="Enrollment Closes" value={fmt(p.enrollment_closes)} />
                            {durationDays != null && <TimelineRow label="Duration" value={`${durationDays} days`} />}
                            {remainingDays != null && <TimelineRow label="Remaining" value={`${remainingDays} days`} />}
                          </Stack>
                        </Box>

                        {/* Engineering Team */}
                        <Box sx={{ p: 3, borderRadius: '24px', bgcolor: '#F8FAFC', border: '1px solid #E2E8F0' }}>
                          <Typography variant="h6" sx={{ fontWeight: 800, mb: 2 }}>Engineering Team</Typography>
                          <Stack spacing={1.25} sx={{ mb: roles.length > 0 ? 2 : 0 }}>
                            <TimelineRow label="Recommended Team Size" value={p.default_team_size ? `${p.default_team_size} members` : 'Not provided'} />
                            <TimelineRow label="Target Capacity" value={p.default_team_size ? `${p.default_team_size} members / version` : 'Not provided'} />
                            {p.total_versions != null && <TimelineRow label="Project Versions" value={`${p.total_versions}`} />}
                          </Stack>
                          {roles.length > 0 && (
                            <Box>
                              <Typography variant="caption" sx={{ fontWeight: 800, color: '#94A3B8', textTransform: 'uppercase', display: 'block', mb: 1 }}>Engineering Roles</Typography>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                                {roles.map((r) => (
                                  <Chip key={r} label={r} size="small" sx={{ bgcolor: 'rgba(108,59,255,0.08)', color: '#5A2EE5', fontWeight: 700, fontSize: '11px' }} />
                                ))}
                              </Box>
                            </Box>
                          )}
                        </Box>

                        {/* Technical specifications */}
                        <Box sx={{ p: 3, borderRadius: '24px', bgcolor: '#F8FAFC', border: '1px solid #E2E8F0' }}>
                          <Typography variant="h6" sx={{ fontWeight: 800, mb: 2 }}>Technical Specifications</Typography>
                          <Stack spacing={1.25}>
                            <TimelineRow label="Language" value={p.primary_language || 'Not provided'} />
                            <TimelineRow label="Framework" value={p.framework || 'Not provided'} />
                            <TimelineRow label="Database" value={p.database_type || 'Not provided'} />
                            <TimelineRow label="Architecture" value={p.architecture || 'Not provided'} />
                          </Stack>
                          {Array.isArray(p.modules) && p.modules.length > 0 && (
                            <Box sx={{ mt: 2.5 }}>
                              <Typography variant="caption" sx={{ fontWeight: 800, color: '#94A3B8', textTransform: 'uppercase', display: 'block', mb: 1 }}>Major Modules</Typography>
                              <Stack spacing={1}>
                                {p.modules.map((m, i) => (
                                  <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                                    <CheckCircleRoundedIcon sx={{ fontSize: 17, color: '#27AE60' }} />
                                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#334155' }}>{m}</Typography>
                                  </Box>
                                ))}
                              </Stack>
                            </Box>
                          )}
                        </Box>
                      </Stack>
                    </Box>
                  </Box>
                </Paper>
              </Grid>
            );
          })()}
        </AnimatePresence>

        {/* Section 2c: Project Versions — ALL versions of the selected project
            as batch-style cards (like choosing an upcoming release train).
            Auto-assignment means the student never has to pick manually:
            the target card is highlighted, everything else explains itself. */}
        <Grid item xs={12}>
          <SectionHeading
            icon={<LayersRoundedIcon />}
            title="Project Versions"
            subtitle={selectedProject
              ? `All versions of ${selectedProject.name}, sorted by start date. When you enroll, you're automatically placed into the highlighted version — no manual selection needed.`
              : 'Pick a project below to see every upcoming version — start dates, enrollment windows, and available seats.'}
          />
          {(() => {
            const versionsAllowed = parseInt(profile?.subscription_versions_allowed, 10) || 0;
            if (loading) return <Skeleton variant="rectangular" height={72} sx={{ borderRadius: '16px' }} />;

            // A project is selected — show EVERY version as a full card.
            // 2 versions → 2 cards, 12 versions → 12 cards. Never limited.
            if (selectedProject) {
              if (versionsLoading) {
                return (
                  <Grid container spacing={2.5}>
                    {[1, 2, 3, 4].map(n => (
                      <Grid item xs={12} sm={6} lg={3} key={n}>
                        <Skeleton variant="rectangular" height={330} sx={{ borderRadius: '20px' }} />
                      </Grid>
                    ))}
                  </Grid>
                );
              }

              const allVersions = (projectVersionsReal || []).slice();
              if (allVersions.length === 0) {
                return (
                  <Alert severity="warning" sx={{ borderRadius: '12px' }}>
                    No versions have been set up for this project yet. Check back soon.
                  </Alert>
                );
              }

              const autoTarget = allVersions.find(v => v.state?.isAutoAssignedVersion);
              const everythingClosed = !autoTarget;

              return (
                <>
                  {autoTarget && (
                    <Alert
                      icon={<AutoAwesomeRoundedIcon fontSize="inherit" />}
                      severity="info"
                      sx={{ borderRadius: '12px', mb: 2.5, bgcolor: 'rgba(108,59,255,0.06)', color: '#3D1E99', '& .MuiAlert-icon': { color: '#6C3BFF' } }}
                    >
                      Clicking <b>Enroll</b> places you into <b>{autoTarget.name}</b>
                      {autoTarget.start_date
                        ? ` — starting ${new Date(autoTarget.start_date).toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' })}`
                        : ''}
                      {allVersions.findIndex(v => v.id === autoTarget.id) > 0
                        ? '. Earlier versions are already full or closed.' : '.'}
                    </Alert>
                  )}
                  {everythingClosed && (
                    <Alert severity="warning" sx={{ borderRadius: '12px', mb: 2.5 }}>
                      This project is currently full. If you enroll, you'll be added to the waiting
                      list and automatically enrolled into the next available version.
                    </Alert>
                  )}
                  <Grid container spacing={2.5} alignItems="stretch">
                    {allVersions.map((v) => (
                      <Grid item xs={12} sm={6} lg={3} key={v.id} sx={{ display: 'flex' }}>
                        <Box sx={{ width: '100%' }}>
                          <VersionScheduleCard
                            version={v}
                            enrolledVersionId={enrolled?.version_id}
                            onEnroll={handleEnroll}
                            enrolling={submitting}
                            disabled={!versionsAllowed}
                          />
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                  {!versionsAllowed && (
                    <Alert severity="info" sx={{ borderRadius: '12px', mt: 2.5 }}>
                      An active subscription is required to enroll. You can browse every version's
                      schedule and seat availability meanwhile.
                    </Alert>
                  )}
                </>
              );
            }

            if (!versionsAllowed) {
              return (
                <Alert severity="info" sx={{ borderRadius: '12px' }}>
                  Purchase a subscription to enroll. Pick a project below to preview its upcoming versions.
                </Alert>
              );
            }

            // No project picked yet — generic placeholder, no per-version
            // enrollment claims until we actually know the real state.
            return (
              <Grid container spacing={2}>
                {Array.from({ length: versionsAllowed }, (_, i) => i + 1).map(n => (
                  <Grid item xs={6} sm={3} key={n}>
                    <Paper elevation={0} sx={{
                      p: 2, borderRadius: '16px', display: 'flex', alignItems: 'center', gap: 1,
                      border: '1px solid #E2E8F0', bgcolor: '#F8FAFC'
                    }}>
                      <Checkbox checked disabled sx={{ p: 0.5, '&.Mui-disabled': { color: '#94A3B8' } }} />
                      <Box>
                        <Typography variant="body1" sx={{ fontWeight: 800, color: '#0F172A' }}>V{n}.0</Typography>
                        <Typography variant="caption" sx={{ color: '#94A3B8', fontWeight: 600 }}>
                          Included in your plan
                        </Typography>
                      </Box>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            );
          })()}
        </Grid>


        {/* Enrollment Submission */}
        <Grid item xs={12} sx={{ mb: 8 }}>
          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Button
              size="large"
              variant="contained"
              disabled={submitting}
              onClick={handleEnroll}
              startIcon={submitting ? null : <RocketLaunchRoundedIcon />}
              sx={{
                borderRadius: '16px', px: 8, py: 2, fontSize: '1.1rem',
                fontWeight: 800, textTransform: 'none',
                boxShadow: '0 10px 30px rgba(108, 59, 255, 0.3)',
                background: 'linear-gradient(90deg, #6C3BFF 0%, #3D1E99 100%)',
                '&:hover': { background: 'linear-gradient(90deg, #5A2EE5 0%, #2E157D 100%)' }
              }}
            >
              {submitting ? (submitPhase || 'Processing Enrollment...') : 'Enroll into Project'}
            </Button>
            <Typography variant="body2" sx={{ color: '#94A3B8', mt: 2, fontWeight: 500 }}>
              By enrolling, you agree to commit {selectedProject?.duration || 'the project duration'} to the team.
            </Typography>
          </Box>
        </Grid>
      </Grid>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} sx={{ borderRadius: '12px', fontWeight: 600 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default NextGenEnrollment;